/**
 * R-11 — Gemini per-token pricing (code-owned, not runtime config).
 *
 * SINGLE SOURCE OF TRUTH for Gemini token prices: `services/modelPricing.ts`
 * (report `generationTelemetry.estimatedCostUsd`) derives its table from
 * GEMINI_PRICING and delegates tiered pricing to {@link computeCallCost}.
 * Verify against Google's pricing pages periodically.
 *
 * `outputPer1M` is the rate for ALL generated tokens: visible response
 * (`candidatesTokenCount`) PLUS thinking (`thoughtsTokenCount`). Pass the
 * billable output count from `readGeminiUsage` (./geminiUsage) as
 * `responseTokens`, not `candidatesTokenCount` alone.
 */
export const GEMINI_PRICING: Record<string, { inputPer1M: number; outputPer1M: number }> = {
  // Base or ≤200K input-context tier. A distinct >200K tier, where published, lives in GEMINI_PRICING_LONG_CONTEXT
  // below and is selected automatically by computeCallCost on prompt size.
  // Argon global STANDARD price from 2026-12-01. The 50% introductory credit
  // through 2026-11-30 is deliberately excluded from this conservative estimate.
  // One token tier at all input lengths; no Argon long-context override below.
  // Source (2026-10-14): https://cloud.google.com/gemini-enterprise-agent-platform/generative-ai/pricing
  'gemini-4-argon':       { inputPer1M: 4.00, outputPer1M: 20.00 },
  'gemini-3.1-pro-preview': { inputPer1M: 2.00, outputPer1M: 12.00 },
  // gemini-3.8-flash: GA, global-only. Priced at the STANDARD rate that
  // starts 2027-01-01 ($1.50 / $7.50). The introductory rate through
  // 2026-12-31 is $0.75 / $3.75, so this over-counts by 2x until then —
  // deliberately, so spend is never under-counted when the rate steps up.
  // Source: https://ai.google.dev/gemini-api/docs/pricing (read 2026-09-29).
  'gemini-3.8-flash':      { inputPer1M: 1.50, outputPer1M: 7.50 },
  'gemini-2.5-pro':        { inputPer1M: 1.25, outputPer1M: 10.00 },
  'gemini-2.5-flash':      { inputPer1M: 0.30, outputPer1M: 2.50 },
  'gemini-2.5-flash-lite': { inputPer1M: 0.10, outputPer1M: 0.40 },
  'gemini-2.0-flash-001':  { inputPer1M: 0.10, outputPer1M: 0.40 },
  'gemini-1.5-pro':        { inputPer1M: 1.25, outputPer1M: 5.00 },
  'gemini-1.5-flash':      { inputPer1M: 0.075, outputPer1M: 0.30 },
};

/**
 * Long-context (>200K input tokens) pricing tier. When a request's input
 * context exceeds {@link LONG_CONTEXT_INPUT_THRESHOLD}, Google bills ALL of
 * that request's tokens (input and output) at these rates. Only models that
 * publish a distinct >200K tier appear here; any model absent from this map
 * keeps its single ≤200K rate at every prompt size (e.g. gemini-3.8-flash,
 * whose >200K column equals its ≤200K column).
 *
 * Verified 2026-09-30 against both published price pages:
 *   - Vertex: https://cloud.google.com/vertex-ai/generative-ai/pricing
 *     (now served at /gemini-enterprise-agent-platform/generative-ai/pricing)
 *     "Gemini 2.5 Pro": input $1.25 (≤200K) / $2.50 (>200K); "Text output
 *     (response and reasoning)" $10.00 / $15.00. "Gemini 3.1 Pro Preview"
 *     (Global): input $2.00 / $4.00; output $12.00 / $18.00.
 *   - AI Studio: https://ai.google.dev/gemini-api/docs/pricing (page last
 *     updated 2026-09-24) — same figures for both models.
 *
 * CORRECTION (2026-09-30): gemini-2.5-pro was coded here at $4 / $18, which is
 * the gemini-3.1-pro-preview long-context rate; the cost strategy doc
 * (`docs/architecture/sed-report-cost-strategy.md` §4) conflated the two. The
 * published 2.5 Pro >200K rate is $2.50 / $15, so long 2.5 Pro prompts were
 * OVER-counted by 60% on input and 20% on output.
 *
 * gemini-1.5-pro is no longer listed on either page (retired); its row is
 * kept unchanged so historical rows keep pricing.
 */
export const GEMINI_PRICING_LONG_CONTEXT: Record<
  string,
  { inputPer1M: number; outputPer1M: number }
> = {
  'gemini-3.1-pro-preview': { inputPer1M: 4.00, outputPer1M: 18.00 },
  'gemini-2.5-pro':        { inputPer1M: 2.50, outputPer1M: 15.00 },
  'gemini-1.5-pro':        { inputPer1M: 2.50, outputPer1M: 10.00 },
};

/** Input-token threshold above which the long-context pricing tier applies. */
export const LONG_CONTEXT_INPUT_THRESHOLD = 200_000;

/**
 * Historical 2.5-Pro fallback for unknown IDs. Keep explicit new-model rows
 * above: this legacy estimate is not a conservative bound on newer models.
 * Preserve it so admitting a named model does not reprice unknown historical rows.
 */
export const FALLBACK_PRICING = { inputPer1M: 1.25, outputPer1M: 10.00 } as const;

/**
 * USD cost for a single Gemini call.
 *
 * Cost accounting must NEVER crash a run (R-11, strategy §0 safety net), so an
 * unknown model id falls back to Pro-equivalent pricing and warns rather than
 * throwing. Prompts above {@link LONG_CONTEXT_INPUT_THRESHOLD} use the model's
 * long-context tier when one is published.
 */
export function computeCallCost(
  model: string,
  promptTokens: number,
  responseTokens: number,
): number {
  const isLongContext = promptTokens > LONG_CONTEXT_INPUT_THRESHOLD;
  let pricing = isLongContext
    ? GEMINI_PRICING_LONG_CONTEXT[model] ?? GEMINI_PRICING[model]
    : GEMINI_PRICING[model];
  if (!pricing) {
    // Never throw — cost accounting must not be able to crash a generation run.
    console.warn(
      `[geminiCostModel] Unknown model "${model}" — using Pro-equivalent fallback pricing for cost.`,
    );
    pricing = FALLBACK_PRICING;
  }
  return (promptTokens / 1_000_000) * pricing.inputPer1M
    + (responseTokens / 1_000_000) * pricing.outputPer1M;
}

/** Sum call costs grouped by report type (unknown types → `unknown`). */
export function costPerReportType(
  rows: ReadonlyArray<{ reportType?: string | null; costUsd: number }>,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const row of rows) {
    const key = row.reportType?.trim() || 'unknown';
    out[key] = (out[key] ?? 0) + row.costUsd;
  }
  return out;
}

/** Firestore field keys cannot contain dots — sanitize model ids. */
export function sanitizeModelKey(model: string): string {
  return model.replace(/\./g, '_');
}
